import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Image from 'next/image';
import { classnames } from 'tailwindcss-classnames';
import EditingHelpText from 'components/helpers/EditingHelpText';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import Container from 'components/helpers/Container';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';

const MIN_CARDS = 2;
const MAX_CARDS = 3;

export type ActionCardCollectionProps = Sitecore.Override.ComponentBase &
  Components.General.ActionCardCollection.Fields.ActionCardCollection;

const ActionCardCollection = ({ fields, rendering }: ActionCardCollectionProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();

  if (!fields) {
    return <></>;
  }

  const headingProps = propsFromFields(fields);
  const phName =
    Object.keys(rendering.placeholders || {}).find((k) => k.startsWith('action-cards-')) ||
    `action-cards-${rendering.uid}`;
  const cardCount = (rendering.placeholders?.[phName] ?? []).filter(
    (item) => !('type' in item && item.type === 'text/sitecore')
  ).length;
  const isBelowMin = cardCount < MIN_CARDS;
  const isAboveMax = cardCount > MAX_CARDS;

  // On published page, hide the component if out of valid range
  if (!isExperienceEditor && (isBelowMin || isAboveMax)) {
    return <></>;
  }

  return (
    <Container size="standard" dataComponent="authorable/general/actioncardcollection">
      {/* Preload alternate CTA icon states once per collection — priority emits <link rel="preload"> in <head> during SSR. */}
      <span aria-hidden="true" hidden>
        <Image
          src="/-/media/project/nmdp/global/icons/nmdp-arrow-plum-24x24-hover.png"
          alt=""
          width={24}
          height={24}
          priority
        />
        <Image
          src="/-/media/project/nmdp/global/icons/nmdp-arrow-plum-24x24-active.png"
          alt=""
          width={24}
          height={24}
          priority
        />
        <Image
          src="/-/media/project/nmdp/global/icons/nmdp-arrow-plum-24x24-disabled.png"
          alt=""
          width={24}
          height={24}
          priority
        />
      </span>
      <div className={fields?.description.value ? classnames('mb-8') : ''}>
        <Heading {...headingProps} />
      </div>
      {isBelowMin && (
        <EditingHelpText
          text={`Action Card Collection | ${cardCount} of minimum ${MIN_CARDS} cards added.`}
        />
      )}
      {isAboveMax && (
        <EditingHelpText
          text={`Action Card Collection | ${cardCount} cards added, maximum is ${MAX_CARDS}.`}
        />
      )}
      <ul className={classnames('lg:flex', 'flex-wrap', 'gap-5')}>
        <Placeholder name={phName} rendering={rendering} />
        {isExperienceEditor && cardCount === 1 && (
          <li
            className={classnames('flex', 'h-auto', 'flex-col', 'flex-1', 'lg:mb-0', 'mb-5')}
            aria-hidden="true"
          >
            <div
              className={classnames(
                'pt-7',
                'px-6',
                'pb-6',
                'flex',
                'items-center',
                'justify-center',
                'text-center',
                'text-gray-500',
                'flex-grow'
              )}
              style={{ border: '2px dashed #ccc' }}
            >
              Add an Action Card
            </div>
          </li>
        )}
      </ul>
    </Container>
  );
};

export default ActionCardCollection;
